import { defineStore } from 'pinia'
import { useRequest } from 'composables/useRequest.js'

const CACHE_TTL = 5 * 60 * 1000

function basePath(componentId) {
  return `/api/v1/admin/global_components/${componentId}/global_component_versions`
}

// Versions of global components, loaded lazily per component when its row is
// expanded in the admin catalog page — keyed by component id so expanding
// several rows doesn't clobber each other's lists.
export const useAdminGlobalComponentVersionsStore = defineStore('adminGlobalComponentVersions', {
  state: () => ({
    byComponent: {}, // componentId -> { records, loading, error, fetchedAt }
  }),

  actions: {
    entryFor(componentId) {
      this.byComponent[componentId] ||= { records: [], loading: false, error: null, fetchedAt: null }
      return this.byComponent[componentId]
    },

    async fetchIfStale(componentId) {
      const entry = this.entryFor(componentId)
      if (entry.fetchedAt !== null && Date.now() - entry.fetchedAt < CACHE_TTL) return
      await this.fetchVersions(componentId)
    },

    async fetchVersions(componentId) {
      const entry = this.entryFor(componentId)
      entry.loading = true
      entry.error = null
      try {
        const { axiosRequest } = useRequest()
        const res = await axiosRequest('GET', basePath(componentId), {}, false)
        if (!res.js_error && res.response.success) {
          entry.records = res.response.data
          entry.fetchedAt = Date.now()
        } else {
          entry.error = 'Failed to load versions.'
        }
      } catch {
        entry.error = 'Something went wrong.'
      } finally {
        entry.loading = false
      }
    },

    async create(componentId, payload) {
      const { axiosRequest } = useRequest()
      const res = await axiosRequest('POST', basePath(componentId), { global_component_version: payload }, false)
      if (!res.js_error && res.response.success) {
        this.entryFor(componentId).records.unshift(res.response.data)
      }
      return res
    },

    async update(componentId, versionId, payload) {
      const { axiosRequest } = useRequest()
      const res = await axiosRequest('PUT', `${basePath(componentId)}/${versionId}`,
        { global_component_version: payload }, false)
      if (!res.js_error && res.response.success) this.applyRecord(componentId, res.response.data)
      return res
    },

    // action is one of 'approve' | 'reject' | 'unapprove' (reset to pending)
    async setStatus(componentId, versionId, action) {
      const { axiosRequest } = useRequest()
      const res = await axiosRequest('PUT', `${basePath(componentId)}/${versionId}/${action}`, {}, false)
      if (!res.js_error && res.response.success) this.applyRecord(componentId, res.response.data)
      return res
    },

    applyRecord(componentId, updated) {
      const { records } = this.entryFor(componentId)
      const index = records.findIndex((v) => v.id === updated.id)
      if (index !== -1) records[index] = { ...records[index], ...updated }
    },
  },
})
